/**
 * Pure helpers shared by every form surface, on either client. No JSX, no Joy,
 * no t() — this module returns values and translation *keys*, never rendered
 * strings, and it touches no DOM.
 *
 * Each of these was duplicated across the surfaces before the form system
 * existed: the deck-id extraction three times, the tag split four times, and
 * `describeApiError` not at all on five of seven, which is why a failed save
 * used to render as a bare "AxiosError" or, on the visual card, as nothing.
 *
 * The scroll and focus helpers that used to sit beside these stayed behind in
 * `src/components/Common/Form/formUtils.js` (MOB-003B). They operate on DOM
 * nodes, so they are the web client's, not the shared layer's.
 */

// The native date input and the timeframe Select both use '' as their "empty"
// value. The API's Goal model types these as Optional[datetime] / Optional[int]
// and Pydantic v2 does not coerce '' to null — it rejects the request with 422.
// Anything the user left blank must therefore travel as an explicit null.
export const emptyToNull = (value) => (value === '' || value === undefined ? null : value)

// FastAPI returns `detail` as an array of {loc, msg} objects for a 422 and as a
// plain string for handled HTTPExceptions. Flatten both into one readable line
// so the user sees which field was rejected instead of a bare "AxiosError".
export const describeApiError = (error) => {
  const detail = error?.response?.data?.detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = (item?.loc || []).filter((part) => part !== 'body').join('.')
        return field ? `${field}: ${item?.msg}` : item?.msg
      })
      .filter(Boolean)
      .join(' · ')
  }
  if (typeof detail === 'string') return detail
  // A refusal with a stable code (ADR-037, ADR-038, ADR-041): the code is more
  // useful than the transport's message. Surfaces that know the code translate it.
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') return detail.code
  return error?.message || ''
}

/**
 * Did this request fail because there was no network, rather than because the
 * server said no?
 *
 * The difference matters to what the user is told. A 422 has a message worth
 * reading; a phone in a tunnel produces the bare string "Network Error", which
 * is English, is not a sentence, and tells the user nothing they can act on.
 * A caller that knows the difference can say "you appear to be offline" in the
 * user's own language instead.
 *
 * Axios sets no `response` when the request never reached a server, and marks
 * the two ways that happens with a code: no route out, or a timeout waiting.
 */
export const isOfflineError = (error) => {
  if (!error || error.response) return false
  return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || Boolean(error.request)
}

/**
 * A card's `deck_id` comes back populated as an object on some responses and as
 * a bare id string on others, so every card form hand-rolled the same ten-line
 * unwrap. One rule, one place.
 */
export const extractDeckId = (deckId) => {
  if (!deckId) return ''
  if (typeof deckId === 'object') return deckId._id || deckId.id || ''
  return String(deckId)
}

/**
 * Comma-separated input → the `string[]` the API already expects.
 *
 * Duplicates are dropped: the chip input (§5.7) renders each tag once, so
 * letting two identical chips through would produce a list with no way to tell
 * which delete control removes which.
 */
export const parseTagInput = (value) => {
  if (Array.isArray(value)) return [...new Set(value.map((tag) => String(tag).trim()).filter(Boolean))]
  if (!value) return []
  return [
    ...new Set(
      String(value)
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    )
  ]
}
